import { useEffect, useState } from "react";
import http from "@/api";
import { useAuth } from "@/auth";
import { Loader2 } from "lucide-react";

const cellStyle = (pct) => {
  if (pct == null) return { background: "#F1F0EC", color: "#A5A399" };
  if (pct >= 90) return { background: "#DCFCE7", color: "#166534" };
  if (pct >= 70) return { background: "#DBEAFE", color: "#1E40AF" };
  if (pct >= 40) return { background: "#FEF3C7", color: "#92400E" };
  return { background: "#FEE2E2", color: "#991B1B" };
};

const LEGEND = [
  { label: "≥ 90%", ...cellStyle(95) },
  { label: "70–89%", ...cellStyle(75) },
  { label: "40–69%", ...cellStyle(50) },
  { label: "< 40%", ...cellStyle(10) },
  { label: "No asignado", ...cellStyle(null) },
];

export default function CompletenessHeatmap() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    setLoading(true);
    http.get("/admin/capture-completeness", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user, period]);

  if (!user || user.role !== "admin") return null;

  const evaluators = data?.evaluators || [];
  const pillars = data?.pillars || [];
  const cells = data?.cells || {};

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-10" data-testid="completeness-heatmap">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">seguimiento del mes</div>
          <h3 className="font-display text-2xl">Completitud de captura por pilar y evaluador</h3>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            % de celdas capturadas (nivel Real) sobre indicadores aplicables al periodo × proveedores.
            Los indicadores trimestrales / semestrales / anuales solo cuentan en su mes correspondiente.
          </p>
        </div>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Periodo</div>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="input-field" data-testid="heatmap-period-input" />
        </label>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[var(--ink-soft)] py-6" data-testid="heatmap-loading">
          <Loader2 size={14} className="animate-spin" /> Calculando completitud…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-lg border border-[var(--line)]" data-testid="heatmap-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">
                  <th className="px-3 py-2">Pilar</th>
                  {evaluators.map((ev) => (
                    <th key={ev.id} className="px-3 py-2 text-center" data-testid={`heatmap-col-${ev.id}`}>
                      {ev.name}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center bg-[var(--cream-soft)]">Total pilar</th>
                </tr>
              </thead>
              <tbody>
                {pillars.map((p) => (
                  <tr key={p.pillar} className="border-t border-[var(--line)]" data-testid={`heatmap-row-${p.pillar}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{p.label}</div>
                      <div className="text-[10px] text-[var(--ink-soft)]">
                        {p.applicable_indicators} indicadores aplicables
                      </div>
                    </td>
                    {evaluators.map((ev) => {
                      const cell = cells[ev.id]?.[p.pillar];
                      const pct = cell?.pct ?? null;
                      const assigned = (ev.assigned_pillars || []).includes(p.pillar);
                      return (
                        <td key={ev.id} className="px-2 py-1.5 text-center"
                          data-testid={`heatmap-cell-${p.pillar}-${ev.id}`}>
                          <div className="rounded-md px-2 py-1.5 font-bold text-xs" style={cellStyle(assigned ? (pct ?? 0) : null)}>
                            {assigned ? (
                              <>
                                {(pct ?? 0).toFixed(0)}%
                                <div className="font-normal text-[10px] opacity-80">
                                  {cell?.captured ?? 0}/{cell?.expected ?? 0}
                                </div>
                              </>
                            ) : "—"}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center bg-[var(--cream-soft)]" data-testid={`heatmap-total-${p.pillar}`}>
                      <div className="rounded-md px-2 py-1.5 font-bold text-xs" style={cellStyle(p.pct ?? 0)}>
                        {(p.pct ?? 0).toFixed(0)}%
                        <div className="font-normal text-[10px] opacity-80">{p.captured}/{p.expected}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3" data-testid="heatmap-legend">
            {LEGEND.map((l) => (
              <span key={l.label} className="inline-flex items-center gap-1.5 text-[11px] text-[var(--ink-soft)]">
                <span className="w-3.5 h-3.5 rounded inline-block" style={{ background: l.background }} />
                {l.label}
              </span>
            ))}
            <span className="text-[11px] text-[var(--ink-soft)] ml-auto">
              {data.suppliers_count} proveedores en el programa
            </span>
          </div>
          {evaluators.length === 0 && (
            <div className="text-sm text-[var(--ink-soft)] italic mt-3" data-testid="heatmap-no-evaluators">
              Aún no hay evaluadores con pilares asignados. Asígnalos en Configuración → Usuarios y permisos.
            </div>
          )}
        </>
      )}
    </div>
  );
}
